import { Location } from '@angular/common';
import { inject, Service, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  Event,
  NavigationEnd,
  Router,
  RoutesRecognized,
} from '@angular/router';
import { ROUTE_NAMES } from '@constants';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
} from 'rxjs';

import { getRelayStatePath } from '@shared/utils/relay-state-path';
import { AppState, setNavigationRoute } from '@store';

interface StoredRoute {
  url: string;
  userId: Nullable<number>;
}

const PREVIOUS_ROUTE_KEY = 'previousRoute';
const SESSION_RESTORE_KEY = 'previousRoute_session';

const isStoredRoute = (value: unknown): value is StoredRoute => {
  const route = value as Nullable<Partial<StoredRoute>>;

  return (
    typeof route?.url === 'string' &&
    (typeof route.userId === 'number' || route.userId === null)
  );
};

const readStoredRoute = (key: string): Nullable<StoredRoute> => {
  try {
    const stored: unknown = JSON.parse(
      window.localStorage.getItem(key) ?? 'null'
    );

    return isStoredRoute(stored) ? stored : null;
  } catch {
    return null;
  }
};

const writeStoredRoute = (key: string, route: StoredRoute) =>
  window.localStorage.setItem(key, JSON.stringify(route));

const isPageReload = () =>
  performance
    .getEntriesByType('navigation')
    .some((entry) => 'type' in entry && entry.type === 'reload');

@Service()
export class RoutesService {
  router = inject(Router);
  location = inject(Location);
  activatedRoute = inject(ActivatedRoute);
  store = inject<Store<AppState>>(Store);

  publicPaths = [
    '',
    '/',
    '/login',
    '/finma-communities',
    '/finma-registration',
    '/registration',
    '/activation',
    '/cec',
    '/503',
    '/404',
    '/price-calculator/sign',
    '/price-calculator/project',
    '/price-calculator/board',
  ];
  previousRoutePath = signal(
    readStoredRoute(PREVIOUS_ROUTE_KEY)?.url ?? '/home'
  );
  isLoggedIn!: boolean;
  userId: Nullable<number> = null;
  lastNavigationPath = new BehaviorSubject<string>('');

  constructor() {
    [PREVIOUS_ROUTE_KEY, SESSION_RESTORE_KEY]
      .filter(
        (key) =>
          window.localStorage.getItem(key) !== null && !readStoredRoute(key)
      )
      .forEach((key) => window.localStorage.removeItem(key));

    this.store
      .select((state) => state)
      .pipe(takeUntilDestroyed())
      .subscribe((state) => {
        this.isLoggedIn = state.auth.isLoggedIn;
        this.userId = state.user.userId ?? null;
      });

    this.store
      .select((state) => state.user.userId)
      .pipe(
        distinctUntilChanged(),
        filter((userId): userId is number => !!userId),
        takeUntilDestroyed()
      )
      .subscribe((userId) => this.keepOnlyRoutesOf(userId));

    this.lastNavigationPath.next(this.location.path());

    this.router.events
      .pipe(
        filter(
          (e: Event): e is RoutesRecognized => e instanceof RoutesRecognized
        ),
        pairwise(),
        distinctUntilChanged()
      )
      .subscribe(([prevEvent, currentEvent]) => {
        const prevRoute = prevEvent.urlAfterRedirects;
        const currentRoute = currentEvent.urlAfterRedirects;

        if (
          this.isLoggedIn &&
          !this.getIsPublicPath(prevRoute) &&
          prevRoute !== this.previousRoutePath() &&
          prevRoute !== currentRoute
        ) {
          this.setPreviousPath(prevRoute);
        }

        this.lastNavigationPath.next(currentRoute);
      });

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        map(() =>
          this.getNavigationRouteFromSnapshot(this.activatedRoute.root)
        ),
        filter((route): route is ROUTE_NAMES => !!route),
        distinctUntilChanged()
      )
      .subscribe((navigationRoute) => {
        this.store.dispatch(
          setNavigationRoute({
            payload: navigationRoute,
          })
        );
      });

    const initialPath = this.location.path() || '/home';
    const relayStatePath = getRelayStatePath(initialPath);

    this.forgetSessionRestoreForOpenedLink(initialPath);
    this.setPreviousPath(initialPath);

    if (relayStatePath) {
      this.saveRequestedUrlForNextLogin(relayStatePath);
    }
  }

  getNavigationRouteFromSnapshot = (
    route: ActivatedRoute
  ): Nullable<ROUTE_NAMES> => {
    if (route.snapshot.data?.navigationRoute) {
      return route.snapshot.data.navigationRoute;
    }

    if (route.parent && route.parent.snapshot.data?.navigationRoute) {
      return route.parent.snapshot.data.navigationRoute;
    }

    if (route.firstChild) {
      return this.getNavigationRouteFromSnapshot(route.firstChild);
    }

    return null;
  };

  setPreviousPath = (path: string) => {
    if (!this.getIsPublicPath(path)) {
      this.storePreviousRoute(this.stripAuthParams(path));
    }
  };

  storePreviousRoute = (url: string) => {
    writeStoredRoute(PREVIOUS_ROUTE_KEY, {
      url,
      userId: this.getLoggedInUserId(),
    });
    this.previousRoutePath.set(url);
  };

  getLoggedInUserId = () => (this.isLoggedIn ? this.userId : null);

  keepOnlyRoutesOf = (userId: number) => {
    [PREVIOUS_ROUTE_KEY, SESSION_RESTORE_KEY].forEach((key) => {
      const route = readStoredRoute(key);

      if (route?.userId === null) {
        writeStoredRoute(key, { ...route, userId });
      } else if (route && route.userId !== userId) {
        window.localStorage.removeItem(key);

        if (key === PREVIOUS_ROUTE_KEY) {
          this.previousRoutePath.set('');
        }
      }
    });
  };

  getUnclaimedPreviousPath = () =>
    readStoredRoute(PREVIOUS_ROUTE_KEY)?.userId ? '' : this.previousRoutePath();

  saveCurrentUrlForSessionRestore = () => {
    const currentUrl = this.stripAuthParams(this.router.url);

    if (!this.getIsPublicPath(currentUrl)) {
      writeStoredRoute(SESSION_RESTORE_KEY, {
        url: currentUrl,
        userId: this.getLoggedInUserId(),
      });
    }
  };

  saveRequestedUrlForNextLogin = (url: string) => {
    const requestedUrl = this.stripAuthParams(url);

    if (!this.getIsPublicPath(requestedUrl)) {
      writeStoredRoute(SESSION_RESTORE_KEY, {
        url: requestedUrl,
        userId: null,
      });
    }
  };

  getSessionRestoreUrl = () =>
    readStoredRoute(SESSION_RESTORE_KEY)?.url || this.previousRoutePath();

  clearSessionRestoreUrl = () => {
    window.localStorage.removeItem(SESSION_RESTORE_KEY);
  };

  forgetSessionRestoreForOpenedLink = (entryPath: string) => {
    const opensLink =
      !isPageReload() &&
      !this.getIsPublicPath(entryPath) &&
      this.stripAuthParams(entryPath) !== '/home';

    if (opensLink) {
      this.clearSessionRestoreUrl();
    }
  };

  getIsPublicPath = (path: string) => {
    const [pathWithoutQuery = ''] = path.split('?');

    return this.publicPaths.indexOf(pathWithoutQuery) !== -1;
  };

  getPostAuthRedirect = () => {
    const sessionRestoreUrl = this.getSessionRestoreUrl();

    this.clearSessionRestoreUrl();

    if (sessionRestoreUrl && sessionRestoreUrl !== this.previousRoutePath()) {
      return this.stripAuthParams(sessionRestoreUrl);
    }

    return this.stripAuthParams(this.previousRoutePath()) || '/home';
  };

  resetPreviousRoute = () => {
    window.localStorage.removeItem(PREVIOUS_ROUTE_KEY);
    this.previousRoutePath.set('');
  };

  getPreviousRouteQueryParams = () =>
    this.router.parseUrl(this.previousRoutePath()).queryParams;

  stripAuthParams = (url: string): string => {
    const [path = '', queryString] = url.split('?');

    if (!queryString) {
      return url;
    }

    const params = new URLSearchParams(queryString);

    params.delete('authKey');
    params.delete('community');
    params.delete('username');
    params.delete('RelayState');

    const remaining = params.toString();

    return remaining ? `${path}?${remaining}` : path;
  };
}
